/* fmt.js — formatting helpers exposed as window.AC.
   money() reads the live currency, which api.js sets from the snapshot settings. */
(function () {
  let currency = "EUR";

  function iso(v) { return v ? String(v).slice(0, 10) : ""; }
  function decimal(v) {
    if (v === null || v === undefined || v === "") return "0";
    const n = Number(v);
    return Number.isFinite(n) ? n.toString() : "0";
  }
  function amount(v) {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "0,00";
  }
  function percent(v) { return `${decimal((Number(v) || 0) * 100)}%`; }
  function fmtDate(v) {
    const s = iso(v);
    if (!s) return "—";
    const p = s.split("-");
    return `${p[2]}.${p[1]}.${p[0].slice(2)}`;
  }
  function hasTime(v) {
    return /(?:T|\s)\d{2}:\d{2}/.test(String(v || ""));
  }
  function fmtDateTime(v) {
    const date = fmtDate(v);
    if (date === "—" || !hasTime(v)) return date;
    const parsed = new Date(String(v));
    if (Number.isNaN(parsed.getTime())) {
      const match = String(v).match(/(?:T|\s)(\d{2}:\d{2})/);
      return match ? `${date} ${match[1]}` : date;
    }
    const time = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(parsed);
    return `${date} ${time}`;
  }
  const PROJECT_TICKET_CLUSTER_NAMES = ["UI", "Backend", "Database", "Infrastructure", "QA", "Documentation"];
  const PROJECT_TICKET_CATEGORY_LABELS = [
    { value: "BacklogItem", label: "Backlog item" },
    { value: "Bug", label: "Bug" },
    { value: "WorkPackage", label: "Work package" },
    { value: "Chore", label: "Chore" },
    { value: "Documentation", label: "Documentation" },
    { value: "Research", label: "Research" },
    { value: "Other", label: "Other" },
  ];
  const PROJECT_TICKET_CATEGORY_OPTIONS = [
    { value: "BacklogItem", label: "Backlog item" },
    { value: "Bug", label: "Bug" },
    { value: "WorkPackage", label: "Work package" },
    { value: "Documentation", label: "Documentation" },
  ];
  const GENERIC_TICKET_CATEGORY_OPTIONS = [
    { value: "BacklogItem", label: "Task" },
    { value: "Research", label: "Research" },
  ];
  const PROJECT_TICKET_CATEGORIES = PROJECT_TICKET_CATEGORY_LABELS.map((option) => option.value);
  const PROJECT_TICKET_STATUSES = ["Open", "In progress", "Partially done", "Ready for review", "Missing info", "Blocked", "Done"];
  const PROJECT_REQUIREMENT_TYPES = ["Functional", "Non-functional", "UI/UX", "Backend", "Data", "Integration", "QA", "Operations"];
  const PROJECT_REQUIREMENT_PRIORITIES = ["Low", "Medium", "High", "Critical"];
  const PROJECT_REQUIREMENT_STATUSES = ["draft", "open question", "accepted", "implemented"];
  const PROJECT_TECH_STACK_CATEGORIES = ["UI", "Backend", "Persistence", "Auth", "Infrastructure", "Testing", "Messaging", "Analytics", "DevOps", "Other"];
  const CORE_WORK_PACKAGE_NAME = "Core app";
  const SOFTWARE_PROJECT_PHASES = ["Gathering/Intake", "Design/OKF Snapshot", "Specs", "Planning/Tickets", "Implementation", "Done"];
  const PROJECT_PROFILES = {
    Software: {
      key: "Software",
      kinds: ["Software", "Implementation"],
      itemLabel: "Work package",
      defaultWorkPackageName: CORE_WORK_PACKAGE_NAME,
      phases: SOFTWARE_PROJECT_PHASES,
      ticketClusters: PROJECT_TICKET_CLUSTER_NAMES,
      ticketStatuses: PROJECT_TICKET_STATUSES,
      ticketCategoryOptions: PROJECT_TICKET_CATEGORY_OPTIONS,
      softwareFields: true,
    },
    Generic: {
      key: "Generic",
      kinds: ["Generic"],
      itemLabel: "Area",
      defaultWorkPackageName: "General",
      phases: ["Open", "Done"],
      ticketClusters: ["Scope", "Tasks", "Decisions", "Follow-up"],
      ticketStatuses: ["Open", "In progress", "Blocked", "Done"],
      ticketCategoryOptions: GENERIC_TICKET_CATEGORY_OPTIONS,
      softwareFields: false,
    },
  };
  const PROJECT_PROFILE_OPTIONS = [PROJECT_PROFILES.Generic, PROJECT_PROFILES.Software];
  function projectProfileKey(value) {
    const kind = typeof value === "object" && value !== null ? value.kind : value;
    const clean = String(kind || "").trim();
    return PROJECT_PROFILES.Generic.kinds.some((item) => item.toLowerCase() === clean.toLowerCase()) ? "Generic" : "Software";
  }
  function projectProfile(value) {
    return PROJECT_PROFILES[projectProfileKey(value)];
  }
  function projectPhases(project) {
    return projectProfile(project).phases;
  }
  function cleanProjectPhase(value, project) {
    const phases = projectPhases(project);
    const raw = String(value || "").trim();
    const clean = raw.toLowerCase();
    const exact = phases.find((item) => item.toLowerCase() === clean);
    if (exact) return exact;
    if (projectHasSoftwareFields(project)) {
      if (clean === "gathering" || clean === "intake") return "Gathering/Intake";
      if (clean === "design" || clean === "okf snapshot") return "Design/OKF Snapshot";
      if (clean === "planning" || clean === "tickets") return "Planning/Tickets";
    }
    return phases[0];
  }
  function projectTicketStatuses(project) {
    return projectProfile(project).ticketStatuses;
  }
  function projectTicketCategoryOptions(project) {
    return projectProfile(project).ticketCategoryOptions || PROJECT_TICKET_CATEGORY_OPTIONS;
  }
  function projectTicketClusterNames(project) {
    return projectProfile(project).ticketClusters;
  }
  function projectItemLabel(project) {
    return projectProfile(project).itemLabel;
  }
  function projectDefaultWorkPackageName(project) {
    return projectProfile(project).defaultWorkPackageName;
  }
  function projectHasSoftwareFields(project) {
    return projectProfile(project).softwareFields;
  }
  function cleanProjectTicketCategory(value) {
    return PROJECT_TICKET_CATEGORIES.includes(value) ? value : "BacklogItem";
  }
  function projectTicketCategoryLabel(value, project) {
    const category = cleanProjectTicketCategory(value);
    const option = projectTicketCategoryOptions(project).find((item) => item.value === category)
      || PROJECT_TICKET_CATEGORY_LABELS.find((item) => item.value === category);
    return option ? option.label : category;
  }
  function cleanProjectRequirementType(value) {
    const raw = String(value || "").trim();
    if (!raw) return PROJECT_REQUIREMENT_TYPES[0];
    const option = PROJECT_REQUIREMENT_TYPES.find((item) => item.toLowerCase() === raw.toLowerCase());
    return option || raw;
  }
  function cleanProjectRequirementPriority(value) {
    const raw = String(value || "").trim().toLowerCase();
    return PROJECT_REQUIREMENT_PRIORITIES.find((item) => item.toLowerCase() === raw) || "Medium";
  }
  function cleanProjectRequirementStatus(value) {
    const raw = String(value || "").trim().toLowerCase();
    return PROJECT_REQUIREMENT_STATUSES.includes(raw) ? raw : "draft";
  }
  function cleanProjectTechStackCategory(value) {
    const raw = String(value || "").trim();
    if (!raw) return "Other";
    return PROJECT_TECH_STACK_CATEGORIES.find((item) => item.toLowerCase() === raw.toLowerCase()) || "Other";
  }

  function projectGuid() {
    return crypto.randomUUID ? crypto.randomUUID() : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (Number(c) ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> Number(c) / 4).toString(16));
  }

  function newProjectTicket(category) {
    return {
      id: projectGuid(),
      title: "",
      category: cleanProjectTicketCategory(category),
      done: false,
      status: "Open",
      remarks: "",
      after: "",
      milestoneId: "",
      gitCommitLinks: [],
      messages: [],
    };
  }

  function newProjectWorkPackage(name, project) {
    return {
      id: projectGuid(),
      position: 0,
      name: name || projectDefaultWorkPackageName(project),
      phase: projectPhases(project)[0],
      createTests: false,
      planningNotes: "",
      ticketClusters: [],
    };
  }

  function normalizeProjectWorkPackages(sections, legacyPlanningNotes, project) {
    const profile = projectProfile(project);
    const source = Array.isArray(sections) && sections.length
      ? sections
      : [{ ...newProjectWorkPackage(profile.defaultWorkPackageName, project), planningNotes: legacyPlanningNotes || "" }];
    return source.map((section, index) => ({
      id: section.id || projectGuid(),
      position: index,
      name: String(section.name || (index === 0 ? profile.defaultWorkPackageName : profile.itemLabel)).trim() || (index === 0 ? profile.defaultWorkPackageName : profile.itemLabel),
      phase: cleanProjectPhase(section.phase || profile.phases[0], project),
      phaseChangedAt: section.phaseChangedAt || null,
      createTests: !!section.createTests,
      planningNotes: section.planningNotes || "",
      ticketClusters: Array.isArray(section.ticketClusters) ? section.ticketClusters : [],
    }));
  }

  function githubRepoSlug(repoUrl) {
    let clean = String(repoUrl || "").trim();
    if (!clean) return "";
    if (/^https?:\/\//i.test(clean) && !/^https?:\/\/github\.com\//i.test(clean)) return "";
    if (/^git@/i.test(clean) && !/^git@github\.com:/i.test(clean)) return "";
    clean = clean.replace(/^git@github\.com:/i, "");
    clean = clean.replace(/^https:\/\/github\.com\//i, "");
    clean = clean.replace(/^http:\/\/github\.com\//i, "");
    clean = clean.replace(/^\/+|\/+$/g, "");
    clean = clean.replace(/\.git$/i, "");
    const parts = clean.split("/").filter(Boolean);
    if (parts.length < 2) return "";
    const owner = parts[0];
    const repo = parts[1].replace(/\.git$/i, "");
    const validSegment = (segment) => /^[A-Za-z0-9_.-]+$/.test(segment);
    return validSegment(owner) && validSegment(repo) ? `${owner}/${repo}` : "";
  }

  function githubCodexAccessUrl(repoUrl) {
    const repoSlug = githubRepoSlug(repoUrl);
    if (!repoSlug) return "";
    const url = new URL("https://github.com/apps/chatgpt-codex-connector/installations/select_target");
    url.searchParams.set("state", `repo=${repoSlug}`);
    return url.toString();
  }

  window.AC = {
    setCurrency(c) { currency = c || "EUR"; },
    get currency() { return currency; },
    money: (v) => `${amount(v)} ${currency}`,
    amount,
    decimal,
    percent,
    fmtDate,
    fmtDateTime,
    fmtDateLong: (v) => { const s = iso(v); if (!s) return "—"; return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(s + "T00:00:00")); },
    TODAY: new Date().toISOString().slice(0, 10),
    todayIso: () => new Date().toISOString().slice(0, 10),
    PROJECT_TICKET_CATEGORIES,
    PROJECT_TICKET_CATEGORY_OPTIONS,
    PROJECT_TICKET_CLUSTER_NAMES,
    PROJECT_TICKET_STATUSES,
    PROJECT_REQUIREMENT_TYPES,
    PROJECT_REQUIREMENT_PRIORITIES,
    PROJECT_REQUIREMENT_STATUSES,
    PROJECT_TECH_STACK_CATEGORIES,
    CORE_WORK_PACKAGE_NAME,
    PROJECT_PROFILES,
    SOFTWARE_PROJECT_PHASES,
    PROJECT_PROFILE_OPTIONS,
    projectProfileKey,
    projectProfile,
    projectPhases,
    cleanProjectPhase,
    projectTicketStatuses,
    projectTicketCategoryOptions,
    projectTicketClusterNames,
    projectItemLabel,
    projectDefaultWorkPackageName,
    projectHasSoftwareFields,
    cleanProjectTicketCategory,
    projectTicketCategoryLabel,
    cleanProjectRequirementType,
    cleanProjectRequirementPriority,
    cleanProjectRequirementStatus,
    cleanProjectTechStackCategory,
    newProjectTicket,
    newProjectWorkPackage,
    normalizeProjectWorkPackages,
    githubRepoSlug,
    githubCodexAccessUrl,
  };
})();
